import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HttpService } from 'src/app/http.service';
import { HttpDownloadProgressEvent, HttpEvent, HttpEventType } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  isStreaming?: boolean;
  toolStatus?: string; // e.g. "Looking up search_player..."
}

interface Category {
  id: number;
  name: string;
  name_cn: string;
  description: string;
  description_cn: string;
  icon: string;
}

@Component({
  selector: 'app-ai-chatbot',
  templateUrl: './ai-chatbot.component.html',
  styleUrls: ['./ai-chatbot.component.scss']
})
export class AiChatbotComponent implements OnDestroy {
  enabled = !environment.production;
  isOpen = false;
  selectedCategory: Category | null = null;
  categories: Category[] = [];
  messages: ChatMessage[] = [];
  inputText = '';
  isLoading = false;
  walletBalance: number | null = null;

  @ViewChild('messageContainer') messageContainer: ElementRef;

  private streamSub: Subscription | null = null;
  private lastParsedOffset = 0;

  private companySub: any;
  private companyUuid: string = '';

  constructor(private http: HttpService, private router: Router) {
    // Load categories once company is set, and reload on company switch
    this.companySub = this.http.getSelectedCompany().subscribe((v: any) => {
      if (v) {
        this.companyUuid = v;
        this.resetAll();
        this.loadCategories();
        this.loadWalletBalance();
      }
    });
  }

  ngOnDestroy() {
    this.streamSub?.unsubscribe();
    this.companySub?.unsubscribe();
  }

  private resetAll() {
    this.selectedCategory = null;
    this.messages = [];
    this.isLoading = false;
    this.inputText = '';
    this.streamSub?.unsubscribe();
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

  close() {
    this.isOpen = false;
  }

  async loadCategories() {
    const res: any = await this.http.post('/admin/ai/categories');
    if (res && res.categories) {
      this.categories = res.categories;
    }
  }

  async loadWalletBalance() {
    try {
      const res: any = await this.http.post('/admin/ai/wallet/balance');
      if (res) {
        this.walletBalance = res.balance;
      }
    } catch (e) {
      this.walletBalance = null;
    }
  }

  selectCategory(cat: Category) {
    this.selectedCategory = cat;
    this.messages = [];
    this.messages.push({
      role: 'system',
      content: `Category: ${cat.name}. How can I help you?`
    });
  }

  backToCategories() {
    this.selectedCategory = null;
    this.messages = [];
    this.isLoading = false;
    this.streamSub?.unsubscribe();
  }

  clearChat() {
    if (!this.selectedCategory) return;
    this.streamSub?.unsubscribe();
    this.isLoading = false;
    this.messages = [];
    this.messages.push({
      role: 'system',
      content: `Category: ${this.selectedCategory.name}. How can I help you?`
    });
  }

  sendMessage() {
    if (!this.inputText.trim() || this.isLoading || !this.selectedCategory) return;

    const userMsg = this.inputText.trim();
    this.inputText = '';

    this.messages.push({ role: 'user', content: userMsg });

    // Build message history for the API (exclude system messages and tool status)
    const apiMessages = this.messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role, content: m.content }));

    this.isLoading = true;
    this.lastParsedOffset = 0;

    // Add placeholder for assistant response
    this.messages.push({ role: 'assistant', content: '', isStreaming: true });
    this.scrollToBottom();

    const stream = this.http.postStream('/admin/ai/chat', {
      category_id: this.selectedCategory.id,
      messages: apiMessages
    });

    this.streamSub = stream.subscribe({
      next: (event: HttpEvent<string>) => {
        if (event.type === HttpEventType.DownloadProgress) {
          const partialText = (event as HttpDownloadProgressEvent).partialText || '';
          this.parseStreamChunks(partialText);
        } else if (event.type === HttpEventType.Response) {
          this.finalizeMessage();
        }
      },
      error: (err) => {
        console.error('AI chat stream error:', err);
        this.finalizeMessage();
        const lastMsg = this.messages[this.messages.length - 1];
        if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.content) {
          lastMsg.content = 'Sorry, an error occurred. Please try again.';
        }
      }
    });
  }

  private parseStreamChunks(fullText: string) {
    // Only process new content since last parse
    const newContent = fullText.substring(this.lastParsedOffset);
    this.lastParsedOffset = fullText.length;

    const lines = newContent.split('\n');
    const assistantMsg = this.messages[this.messages.length - 1];

    for (const line of lines) {
      if (!line.trim()) continue;

      try {
        const data = JSON.parse(line);

        if (data.type === 'text') {
          if (assistantMsg && assistantMsg.role === 'assistant') {
            assistantMsg.content += data.content;
            assistantMsg.toolStatus = undefined;
            this.scrollToBottom();
          }
        } else if (data.type === 'tool_status') {
          if (assistantMsg && assistantMsg.role === 'assistant') {
            if (data.status === 'running') {
              assistantMsg.toolStatus = `Looking up ${data.name.replace(/_/g, ' ')}...`;
            } else {
              assistantMsg.toolStatus = undefined;
            }
          }
        } else if (data.type === 'error') {
          if (assistantMsg && assistantMsg.role === 'assistant') {
            assistantMsg.content += `\n\nError: ${data.message}`;
          }
        } else if (data.type === 'navigate' && data.target) {
          this.handleNavigate(data.target);
        } else if (data.type === 'done' && data.tokens) {
          // Refresh wallet balance after cost deduction
          this.loadWalletBalance();
        }
      } catch (e) {
        // Partial JSON line — ignore, will be completed in next chunk
      }
    }
  }

  private finalizeMessage() {
    this.isLoading = false;
    const lastMsg = this.messages[this.messages.length - 1];
    if (lastMsg) {
      lastMsg.isStreaming = false;
      lastMsg.toolStatus = undefined;
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  trackByIndex(index: number) {
    return index;
  }

  private handleNavigate(target: string) {
    const routeMap: Record<string, string> = {
      promotion: 'front/promotion',
    };

    const path = routeMap[target];
    if (!path || !this.companyUuid) return;

    const fullPath = `/lo/${this.companyUuid}/${path}`;
    const currentUrl = this.router.url;

    if (currentUrl.includes(path)) {
      // Already on the page — force reload by navigating away and back
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(fullPath);
      });
    } else {
      this.router.navigateByUrl(fullPath);
    }
  }

  private scrollToBottom() {
    try {
      if (this.messageContainer) {
        const el = this.messageContainer.nativeElement;
        setTimeout(() => { el.scrollTop = el.scrollHeight; }, 50);
      }
    } catch (e) {}
  }
}
